import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Play, CheckCircle2, Clock, AlertTriangle, ArrowRight, Trash2 } from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { Skeleton, SkeletonCard } from '@/components/ui/skeleton.js';
import { PlatformBadge } from '@/components/ui/PlatformBadge.js';
import { PlatformType } from '@/types/index.js';

interface Task {
  id: string;
  project_id: string;
  title: string;
  description: string;
  status: 'todo' | 'in_progress' | 'in_review' | 'blocked' | 'done';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  progress_percent: number;
  due_date: string | null;
  project_name: string;
  project_platform?: PlatformType | string | null;
}

export function MyTasksPage() {
  const { user } = useAuth();
  const role = user?.role || 'team_member';
  const isOwner = role === 'owner';
  const isViewer = role === 'viewer';

  const [tasks, setTasks] = useState<{
    today: Task[];
    upcoming: Task[];
    overdue: Task[];
    in_review: Task[];
    completed: Task[];
  }>({
    today: [],
    upcoming: [],
    overdue: [],
    in_review: [],
    completed: [],
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'today' | 'upcoming' | 'overdue' | 'in_review' | 'completed'>('today');

  const fetchMyTasks = async () => {
    try {
      const res = await api.get<{ data: any }>('/api/v1/my-tasks');
      if (res?.data) {
        setTasks(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch my-tasks', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyTasks();
  }, []);

  const handleDeleteTask = async (taskId: string, title?: string) => {
    const confirmed = window.confirm
      ? window.confirm(`Are you sure you want to delete task "${title || 'this task'}"?`)
      : true;
    if (!confirmed) return;
    try {
      await api.delete(`/api/v1/tasks/${taskId}`);
      fetchMyTasks();
    } catch (e: any) {
      alert(e.message || 'Failed to delete task');
    }
  };

  const handleStartTimer = async (taskId: string) => {
    try {
      await api.post('/api/v1/time/timer/start', { task_id: taskId });
      window.location.reload(); // Refresh to update TopBar timer
    } catch (e: any) {
      alert(e.message || 'Could not start timer');
    }
  };

  const handleUpdateProgress = async (task: Task, newProgress: number) => {
    try {
      await api.patch(`/api/v1/tasks/${task.id}`, {
        progress_percent: newProgress,
        status: newProgress > 0 && task.status === 'todo' ? 'in_progress' : task.status,
      });
      fetchMyTasks();
    } catch (e) {
      console.error(e);
    }
  };

  const handleMoveToReview = async (taskId: string) => {
    try {
      await api.patch(`/api/v1/tasks/${taskId}`, {
        status: 'in_review',
        progress_percent: 90,
      });
      fetchMyTasks();
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="space-y-2">
          <Skeleton className="h-8 w-44" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="flex space-x-2 border-b border-brand-border pb-2">
          <Skeleton className="h-8 w-24 rounded-lg" />
          <Skeleton className="h-8 w-24 rounded-lg" />
          <Skeleton className="h-8 w-24 rounded-lg" />
          <Skeleton className="h-8 w-24 rounded-lg" />
        </div>
        <div className="space-y-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  const currentList = tasks[activeTab] || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-brand-navy">My Tasks</h1>
          <p className="text-sm text-slate-500">Focus on what is due today and track precision progress.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-slate-200">
        {[
          { key: 'today', label: 'Today', count: tasks.today.length, icon: CheckCircle2 },
          { key: 'upcoming', label: 'Upcoming', count: tasks.upcoming.length, icon: ArrowRight },
          { key: 'overdue', label: 'Overdue', count: tasks.overdue.length, icon: AlertTriangle, alert: true },
          { key: 'in_review', label: 'In Review', count: tasks.in_review.length, icon: Clock },
          { key: 'completed', label: 'Completed', count: tasks.completed.length, icon: CheckCircle2 },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex items-center space-x-2 pb-3 px-3 text-sm font-medium border-b-2 transition-colors ${
                isActive
                  ? 'border-brand-teal text-brand-teal'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
              <span
                className={`ml-1 rounded-full px-2 py-0.5 text-xs ${
                  tab.alert && tab.count > 0
                    ? 'bg-red-100 text-red-700'
                    : isActive
                    ? 'bg-teal-50 text-brand-teal'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Tasks List */}
      <div className="space-y-3">
        {currentList.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-slate-500">
              No tasks currently in {activeTab.replace('_', ' ')}. Enjoy your clean slate!
            </CardContent>
          </Card>
        ) : (
          currentList.map((task) => (
            <Card key={task.id} className="hover:border-slate-300 transition-all">
              <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1.5 flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <PlatformBadge platform={task.project_platform || 'direct'} size="xs" />
                    <span className="text-xs font-semibold text-brand-teal uppercase tracking-wide">
                      {task.project_name}
                    </span>
                    <Badge variant={task.priority as any} className="text-[10px] uppercase">
                      {task.priority}
                    </Badge>
                    <Badge variant={task.status as any} className="text-[10px] capitalize">
                      {task.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-sm text-brand-navy truncate">{task.title}</h3>
                  {task.due_date && (
                    <div className="text-xs text-slate-400">
                      Due: {new Date(task.due_date).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {/* Progress Control & Actions */}
                <div className="flex items-center space-x-4">
                  {/* Inline Progress Control */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="range"
                      min="0"
                      max={task.status === 'done' ? 100 : 99}
                      value={task.progress_percent}
                      onChange={(e) => handleUpdateProgress(task, Number(e.target.value))}
                      disabled={isViewer || task.status === 'done'}
                      className="w-24 accent-brand-teal"
                    />
                    <span className="text-xs font-mono font-semibold text-slate-700 w-8 text-right">
                      {task.progress_percent}%
                    </span>
                  </div>

                  {/* Move to Review Button */}
                  {!isViewer && task.status !== 'in_review' && task.status !== 'done' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMoveToReview(task.id)}
                      className="text-xs border-brand-teal text-brand-teal hover:bg-teal-50"
                      title="Submit task for manager approval"
                    >
                      Awaiting Review
                    </Button>
                  )}

                  {/* Start Timer */}
                  {!isViewer && (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => handleStartTimer(task.id)}
                      className="flex items-center space-x-1 bg-brand-teal hover:bg-brand-teal-action text-white text-xs"
                      title="Start precision timer on this task"
                    >
                      <Play className="h-3.5 w-3.5" />
                      <span>Start Timer</span>
                    </Button>
                  )}

                  {/* Delete Task (Owner ONLY) */}
                  {isOwner && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDeleteTask(task.id, task.title)}
                      className="h-8 w-8 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete task"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
